import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// V2 export — Brand-Forward layout (Eskimi-purple cover + editorial brief + URL table).
// Renders the v5 brainstormed mockup off-screen via html2canvas and assembles a
// multi-page A4 PDF. Uses Google Fonts (Oswald, Inter) — the browser handles
// Unicode and font metrics, so no sanitiser is needed (Turkish ı, Polish ł, etc.
// render natively).

interface Sample {
  url: string;
  reasoning: string;
  confidence: number;
  contentSummary: string;
}

interface AnalysisExportDataV2 {
  bulkJob: {
    id: string;
    promptId: string;
    promptName: string;
    createdAt: string;
    totalUrls: number;
    matchedUrls: number;
    unmatchedUrls: number;
    averageConfidence: number;
    totalCost?: number;
  };
  prompt: {
    name: string;
    description: string;
    prompt: string;
    type: 'brand-safety' | 'audience-targeting';
  };
  matchedSamples: Sample[];
  unmatchedSamples: Sample[];
}

// A4 at 96 dpi
const PAGE_W = 794;
const PAGE_H = 1123;
/** Printable height available for table rows on a table page (A4 minus header/h2/legend/footer chrome). */
export const PAGE_CONTENT_HEIGHT = 820;
/** Printable height available for brief blocks (paragraphs/guidelines) on a brief page (A4 minus header/title/footer chrome). */
export const BRIEF_CONTENT_HEIGHT = 760;
/** Fallback row height used when offsetHeight returns 0 (e.g. jsdom, hidden elements). */
const ROW_HEIGHT_FALLBACK = 120;
const FONTS_HREF = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Oswald:wght@500;600;700;800&display=swap';

export type MeasureRowHeight = (row: HTMLElement) => number;

export interface PaginateResult {
  /** One entry per page; each page is an array of row indices from the input rows. */
  pages: number[][];
  /** Measured height of each row, in input order. */
  rowHeights: number[];
}

/**
 * Group row indices into pages by measured height so cumulative height per page
 * never exceeds `pageContentHeight`. Each row is appended to `container` and
 * measured via `measureRowHeight` before being placed.
 *
 * Pure logic — caller owns DOM lifetime of `container`.
 */
export function paginateRows<T>(
  rows: T[],
  buildRowEl: (row: T) => HTMLElement,
  container: HTMLElement,
  pageContentHeight: number = PAGE_CONTENT_HEIGHT,
  measureRowHeight: MeasureRowHeight = (el) => el.offsetHeight || ROW_HEIGHT_FALLBACK,
): PaginateResult {
  const pages: number[][] = [];
  const rowHeights: number[] = [];

  let currentPage: number[] = [];
  let runningHeight = 0;

  for (let i = 0; i < rows.length; i++) {
    const rowEl = buildRowEl(rows[i]);
    container.appendChild(rowEl);
    const h = measureRowHeight(rowEl);
    rowHeights.push(h);

    if (currentPage.length > 0 && runningHeight + h > pageContentHeight) {
      pages.push(currentPage);
      currentPage = [];
      runningHeight = 0;
    }

    currentPage.push(i);
    runningHeight += h;
  }

  if (currentPage.length > 0) {
    pages.push(currentPage);
  }

  return { pages, rowHeights };
}

function escapeHtml(s: string): string {
  return (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function ensureFonts(): Promise<void> {
  if (!document.querySelector(`link[data-pdfv2-fonts]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = FONTS_HREF;
    link.setAttribute('data-pdfv2-fonts', 'true');
    document.head.appendChild(link);
  }
  // Force load by measuring; document.fonts.ready resolves once styled fonts are ready.
  if ((document as any).fonts?.ready) {
    await (document as any).fonts.ready;
  }
  // Extra paint frame to be safe.
  await new Promise<void>(resolve => requestAnimationFrame(() => resolve()));
}

const PDF_STYLES = `
.v2-root, .v2-root * { box-sizing: border-box; }
/* Force light-theme tokens: remove dark class scope and apply :root variables */
.v2-root {
  --canvas: #F4EDFF;
  --surface: #FFFFFF;
  --sidebar: #FFFFFF;
  --line: #ECE3FA;
  --ink: #2C2140;
  --ink-muted: #8B7FA6;
  --accent: #8D5DD9;
  --accent-active: #693BB1;
  --nav-active-bg: #F4EDFF;
  --safe-bg: #E3F7EC;
  --safe-fg: #0E8A4C;
  --warn-bg: #FFF4D6;
  --warn-fg: #A06B00;
  --risk-bg: #FFE9E9;
  --risk-fg: #D43D3D;
  --gradient-accent: linear-gradient(128deg, #8D5DD9, #5A9CFF);
}
.v2-page {
  width: ${PAGE_W}px;
  min-height: ${PAGE_H}px;
  background: #fff;
  color: #1f2125;
  position: relative;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* --------- Cover --------- */
.v2-cover {
  background: linear-gradient(135deg, #8D5DD9 0%, #693BB1 50%, #2D1A55 100%);
  color: #fff;
  padding: 48px 54px 42px;
  height: 100%;
  display: flex; flex-direction: column;
  position: relative; overflow: hidden;
}
.v2-cover::before { content: ''; position: absolute; right: -180px; top: -180px; width: 540px; height: 540px; border-radius: 50%; background: rgba(255,255,255,.06); }
.v2-cover::after  { content: ''; position: absolute; right: -270px; bottom: -300px; width: 720px; height: 720px; border-radius: 50%; background: rgba(255,255,255,.04); }
.v2-cover .v2-head { display: flex; justify-content: space-between; align-items: center; position: relative; z-index: 1; }
.v2-cover .v2-logo { font-family: 'Oswald', sans-serif; font-weight: 800; font-size: 32px; letter-spacing: .04em; }
.v2-cover .v2-logo .v2-sub { font-family: 'Inter', sans-serif; font-weight: 400; font-size: 18px; opacity: .8; margin-left: 10px; letter-spacing: .04em; }
.v2-cover .v2-pgnum { font-size: 15px; opacity: .7; }
.v2-cover .v2-kicker { font-size: 13px; letter-spacing: .3em; text-transform: uppercase; color: rgba(255,255,255,.7); margin-top: 80px; position: relative; z-index: 1; font-weight: 500; }
.v2-cover h1 { font-family: 'Oswald', sans-serif; font-weight: 700; font-size: 80px; line-height: .92; letter-spacing: -.005em; margin: 16px 0 8px; text-transform: uppercase; position: relative; z-index: 1; }
.v2-cover .v2-tagline { font-size: 18px; color: rgba(255,255,255,.85); letter-spacing: .04em; position: relative; z-index: 1; }
.v2-cover .v2-hero { margin-top: auto; padding-top: 38px; border-top: 1px solid rgba(255,255,255,.2); position: relative; z-index: 1; }
.v2-cover .v2-hero-row { display: flex; align-items: baseline; gap: 24px; }
.v2-cover .v2-big { font-family: 'Oswald', sans-serif; font-size: 138px; font-weight: 700; line-height: .9; }
.v2-cover .v2-ctx { font-size: 17px; line-height: 1.45; color: rgba(255,255,255,.85); max-width: 55%; padding-bottom: 8px; }
.v2-cover .v2-ministats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; margin-top: 30px; padding-top: 24px; border-top: 1px solid rgba(255,255,255,.2); position: relative; z-index: 1; }
.v2-cover .v2-mini-n { font-family: 'Oswald', sans-serif; font-size: 46px; font-weight: 700; line-height: 1; }
.v2-cover .v2-mini-l { font-size: 13px; letter-spacing: .2em; text-transform: uppercase; color: rgba(255,255,255,.7); margin-top: 6px; }
.v2-cover .v2-cover-footer { display: flex; justify-content: space-between; margin-top: 26px; padding-top: 16px; border-top: 1px solid rgba(255,255,255,.2); font-size: 12px; color: rgba(255,255,255,.7); position: relative; z-index: 1; letter-spacing: .04em; }

/* --------- Brief --------- */
.v2-brief { padding: 32px 44px; display: flex; flex-direction: column; height: 100%; }
.v2-brief .v2-head { display: flex; justify-content: space-between; align-items: center; padding-bottom: 16px; border-bottom: 2px solid #8D5DD9; }
.v2-brief .v2-head .v2-logo { font-family: 'Oswald', sans-serif; font-weight: 800; color: #693BB1; font-size: 22px; letter-spacing: .04em; }
.v2-brief .v2-head .v2-logo .v2-sub { color: #999; font-family: 'Inter', sans-serif; font-weight: 400; font-size: 15px; margin-left: 8px; letter-spacing: .04em; }
.v2-brief .v2-head .v2-pgnum { font-size: 14px; color: #999; }
.v2-brief .v2-section-label { font-size: 14px; color: #8D5DD9; letter-spacing: .25em; text-transform: uppercase; font-weight: 700; margin-top: 22px; margin-bottom: 6px; }
.v2-brief h2 { font-family: 'Oswald', sans-serif; font-weight: 700; font-size: 42px; margin: 0 0 22px; letter-spacing: -.005em; line-height: 1; }
.v2-brief p { font-size: 14.5px; line-height: 1.65; color: #2c2c2c; margin: 0 0 12px; text-align: justify; }
.v2-g-row { display: grid; grid-template-columns: 40px 250px 1fr; gap: 22px; padding: 14px 0; border-bottom: 1px solid #F4EDFF; align-items: baseline; }
.v2-g-row:last-child { border-bottom: 0; }
.v2-g-num { font-family: 'Oswald', sans-serif; font-size: 22px; font-weight: 700; color: #8D5DD9; line-height: 1; }
.v2-g-title { font-family: 'Oswald', sans-serif; text-transform: uppercase; letter-spacing: .04em; font-size: 16px; color: #693BB1; font-weight: 700; }
.v2-g-desc { font-size: 14.5px; line-height: 1.55; color: #2c2c2c; }
.v2-brief .v2-page-footer { display: flex; justify-content: space-between; margin-top: auto; padding-top: 16px; border-top: 1px solid #E5E5EA; font-size: 12px; color: #999; letter-spacing: .04em; }

/* --------- URL table page --------- */
.v2-tbl-page { padding: 32px 44px; display: flex; flex-direction: column; height: 100%; }
.v2-tbl-page .v2-head { display: flex; justify-content: space-between; align-items: center; padding-bottom: 16px; border-bottom: 2px solid #8D5DD9; }
.v2-tbl-page .v2-head .v2-logo { font-family: 'Oswald', sans-serif; font-weight: 800; color: #693BB1; font-size: 22px; letter-spacing: .04em; }
.v2-tbl-page .v2-head .v2-logo .v2-sub { color: #999; font-family: 'Inter', sans-serif; font-weight: 400; font-size: 15px; margin-left: 8px; letter-spacing: .04em; }
.v2-tbl-page .v2-head .v2-pgnum { font-size: 14px; color: #999; }
.v2-tbl-page h2 { font-family: 'Oswald', sans-serif; font-weight: 700; font-size: 36px; margin: 22px 0 8px; letter-spacing: -.005em; display: flex; align-items: baseline; gap: 14px; }
.v2-tbl-page h2 .v2-count { font-family: 'Inter', sans-serif; font-size: 15px; color: #999; font-weight: 500; letter-spacing: 0; text-transform: none; }
.v2-tbl-page .v2-legend { display: flex; gap: 22px; font-size: 12.5px; color: #555; margin-bottom: 14px; letter-spacing: .04em; }
.v2-tbl-page .v2-legend span { display: inline-flex; align-items: center; gap: 7px; }
.v2-tbl-page .v2-legend .v2-legend-d { width: 12px; height: 12px; border-radius: 50%; }
.v2-tbl-page .v2-legend .v2-legend-d.g { background: #5BC18C; }
.v2-tbl-page .v2-legend .v2-legend-d.r { background: #E07474; }

.v2-tbl { background: #fff; border: 1px solid #E8DBFF; border-radius: 10px; overflow: hidden; }
.v2-thead {
  display: grid;
  grid-template-columns: 18px 1fr 84px;
  gap: 18px;
  padding: 12px 20px;
  background: #F4EDFF;
  border-bottom: 1px solid #E8DBFF;
  font-family: 'Oswald', sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: .14em;
  color: #693BB1;
  font-weight: 700;
}
.v2-tbl-row {
  display: grid;
  grid-template-columns: 18px 1fr 84px;
  gap: 18px;
  padding: 14px 20px;
  border-bottom: 1px solid #F4EDFF;
  align-items: start;
}
.v2-tbl-row:last-child { border-bottom: 0; }
.v2-tbl-row .v2-dot { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; margin-top: 4px; }
.v2-tbl-row .v2-dot.g { background: #5BC18C; }
.v2-tbl-row .v2-dot.r { background: #E07474; }
.v2-stack { min-width: 0; display: flex; flex-direction: column; gap: 5px; }
.v2-stack .v2-url { font-family: 'Inter', sans-serif; font-weight: 600; font-size: 14px; color: #2D1A55; word-break: break-all; line-height: 1.3; }
.v2-stack .v2-reason { font-size: 12.5px; line-height: 1.5; color: #555; }
.v2-conf-cell { display: flex; flex-direction: column; gap: 8px; padding-top: 3px; }
.v2-conf-num { font-family: 'Oswald', sans-serif; font-size: 15px; font-weight: 700; line-height: 1; text-align: right; }
.v2-conf-num.g { color: #2D8C5F; }
.v2-conf-num.r { color: #C44343; }
.v2-conf-bar { width: 100%; height: 4px; background: #F4EDFF; border-radius: 2px; overflow: hidden; }
.v2-conf-bar > i { display: block; height: 100%; }
.v2-conf-bar.g > i { background: #5BC18C; }
.v2-conf-bar.r > i { background: #E07474; }
.v2-tbl-page .v2-page-footer { display: flex; justify-content: space-between; padding-top: 16px; margin-top: 16px; border-top: 1px solid #E5E5EA; font-size: 12px; color: #999; letter-spacing: .04em; }
`;

function pageEl(): HTMLDivElement {
  const p = document.createElement('div');
  p.className = 'v2-page';
  return p;
}

function buildCover(data: AnalysisExportDataV2): HTMLElement {
  const p = pageEl();
  const matchPct = data.bulkJob.totalUrls > 0
    ? (data.bulkJob.matchedUrls / data.bulkJob.totalUrls * 100).toFixed(1)
    : '0.0';
  const promptName = escapeHtml(data.bulkJob.promptName || data.prompt.name || 'Brand Blueprint');
  const total = data.bulkJob.totalUrls.toLocaleString();
  const matched = data.bulkJob.matchedUrls.toLocaleString();
  const unmatched = data.bulkJob.unmatchedUrls.toLocaleString();
  p.innerHTML = `
    <div class="v2-cover">
      <div class="v2-head">
        <div class="v2-logo">ESKIMI <span class="v2-sub">DeepContext</span></div>
        <div class="v2-pgnum">Page 1</div>
      </div>
      <div class="v2-kicker">Brand Blueprint Overview</div>
      <h1>${promptName}</h1>
      <div class="v2-tagline">Brand Safety &middot; Contextual Targeting &middot; Performance</div>
      <div class="v2-hero">
        <div class="v2-hero-row">
          <div class="v2-big">${matchPct}%</div>
          <div class="v2-ctx">match rate across ${total} evaluated URLs against the brief.</div>
        </div>
        <div class="v2-ministats">
          <div><div class="v2-mini-n">${total}</div><div class="v2-mini-l">Total URLs</div></div>
          <div><div class="v2-mini-n">${matched}</div><div class="v2-mini-l">Matched</div></div>
          <div><div class="v2-mini-n">${unmatched}</div><div class="v2-mini-l">Unmatched</div></div>
        </div>
      </div>
      <div class="v2-cover-footer"><span>&copy; ${new Date().getFullYear()} Eskimi. All rights reserved.</span><span>Eskimi DeepContext</span></div>
    </div>
  `;
  return p;
}

interface ParsedBrief {
  paragraphs: string[];
  guidelines: { title: string; desc: string }[];
}

export function parseBrief(promptText: string): ParsedBrief {
  // Extract `**Title:** description.` style bullets as guidelines; everything else
  // becomes paragraphs (split on blank lines).
  const text = (promptText || '').trim();
  const paragraphs: string[] = [];
  const guidelines: { title: string; desc: string }[] = [];

  const lines = text.split(/\r?\n/);
  let buf: string[] = [];
  const flushPara = () => {
    const para = buf.join(' ').trim();
    if (para) paragraphs.push(para);
    buf = [];
  };

  for (const raw of lines) {
    const line = raw.trim();
    const bulletMatch = line.match(/^\*\*([^*]+):\*\*\s*(.+)$/);
    if (bulletMatch) {
      flushPara();
      guidelines.push({ title: bulletMatch[1].trim(), desc: bulletMatch[2].trim() });
    } else if (!line) {
      flushPara();
    } else {
      buf.push(line);
    }
  }
  flushPara();

  return { paragraphs, guidelines };
}

/**
 * A single measurable unit of brief content — one paragraph, the "Guidelines"
 * section label, or one guideline row. Pagination operates on this flat list
 * so a long brief can be split across as many pages as it needs instead of
 * growing one page past A4 (which is what caused the vertical-squash bug:
 * html2canvas captured an over-tall page and jsPDF stamped it at fixed A4
 * dimensions, distorting the aspect ratio).
 */
export type BriefBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'guidelines-label' }
  | { kind: 'guideline'; num: string; title: string; desc: string };

export function buildBriefBlocks(parsed: ParsedBrief): BriefBlock[] {
  const blocks: BriefBlock[] = parsed.paragraphs.map((text) => ({ kind: 'paragraph', text }));
  if (parsed.guidelines.length > 0) {
    blocks.push({ kind: 'guidelines-label' });
    parsed.guidelines.forEach((g, i) => {
      blocks.push({ kind: 'guideline', num: String(i + 1).padStart(2, '0'), title: g.title, desc: g.desc });
    });
  }
  return blocks;
}

/**
 * Build a single brief content element. Exposed so paginateRows can append it
 * to an off-screen container for height measurement.
 */
export function buildBriefBlockEl(block: BriefBlock): HTMLElement {
  if (block.kind === 'paragraph') {
    const el = document.createElement('p');
    el.innerHTML = escapeHtml(block.text).replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    return el;
  }
  if (block.kind === 'guidelines-label') {
    const el = document.createElement('div');
    el.className = 'v2-section-label';
    el.style.marginTop = '18px';
    el.textContent = 'Guidelines';
    return el;
  }
  const el = document.createElement('div');
  el.className = 'v2-g-row';
  el.innerHTML = `
    <div class="v2-g-num">${block.num}</div>
    <div class="v2-g-title">${escapeHtml(block.title)}</div>
    <div class="v2-g-desc">${escapeHtml(block.desc)}</div>
  `;
  return el;
}

function buildBriefPage(
  blockEls: HTMLElement[],
  promptName: string,
  isFirstPage: boolean,
  pdfPageNum: number,
): HTMLElement {
  const p = pageEl();
  const titleHtml = isFirstPage
    ? `<div class="v2-section-label">Campaign Brief</div><h2>${promptName}</h2>`
    : `<div class="v2-section-label">Campaign Brief <span style="opacity:.5">(cont.)</span></div>`;

  p.innerHTML = `
    <div class="v2-brief">
      <div class="v2-head">
        <div class="v2-logo">ESKIMI <span class="v2-sub">DeepContext</span></div>
        <div class="v2-pgnum">Page ${pdfPageNum}</div>
      </div>
      ${titleHtml}
      <div class="v2-brief-body"></div>
      <div class="v2-page-footer"><span>&copy; ${new Date().getFullYear()} Eskimi. All rights reserved.</span><span>Eskimi DeepContext</span></div>
    </div>
  `;
  const body = p.querySelector('.v2-brief-body');
  if (body) blockEls.forEach((el) => body.appendChild(el));
  return p;
}

/**
 * Build a single `.v2-tbl-row` element. Exposed so paginateRows can append it
 * to an off-screen container for height measurement.
 */
export function buildTableRowEl(row: Sample & { isMatch: boolean }): HTMLElement {
  const cls = row.isMatch ? 'g' : 'r';
  const conf = Math.round((row.confidence || 0) * 100);
  const el = document.createElement('div');
  el.className = 'v2-tbl-row';
  el.innerHTML = `
    <span class="v2-dot ${cls}"></span>
    <div class="v2-stack">
      <div class="v2-url">${escapeHtml(row.url)}</div>
      <div class="v2-reason">${escapeHtml(row.reasoning)}</div>
    </div>
    <div class="v2-conf-cell">
      <span class="v2-conf-num ${cls}">${conf}%</span>
      <div class="v2-conf-bar ${cls}"><i style="width:${conf}%"></i></div>
    </div>
  `;
  return el;
}

function buildTablePage(
  rowEls: HTMLElement[],
  pageIndex: number,
  totalSamples: number,
  rowsOnThisPage: number,
  pdfPageNum: number,
): HTMLElement {
  const p = pageEl();
  const countLabel = pageIndex === 1
    ? `${rowsOnThisPage} of ${totalSamples}`
    : 'cont.';

  p.innerHTML = `
    <div class="v2-tbl-page">
      <div class="v2-head">
        <div class="v2-logo">ESKIMI <span class="v2-sub">DeepContext</span></div>
        <div class="v2-pgnum">Page ${pdfPageNum}</div>
      </div>
      <h2>URL Analysis Examples<span class="v2-count">${countLabel}</span></h2>
      <div class="v2-legend">
        <span><span class="v2-legend-d g"></span>Match (matched against brief)</span>
        <span><span class="v2-legend-d r"></span>Mismatch</span>
      </div>
      <div class="v2-tbl">
        <div class="v2-thead"><div></div><div>URL &middot; Reasoning</div><div style="text-align:right">Conf.</div></div>
      </div>
      <div class="v2-page-footer"><span>&copy; ${new Date().getFullYear()} Eskimi. All rights reserved.</span><span>Eskimi DeepContext</span></div>
    </div>
  `;
  // Append measured row elements into the .v2-tbl container after the header row.
  const tbl = p.querySelector('.v2-tbl');
  if (tbl) {
    rowEls.forEach((rowEl) => tbl.appendChild(rowEl));
  }
  return p;
}

export async function generateAnalysisReportV2(data: AnalysisExportDataV2): Promise<void> {
  const root = document.createElement('div');
  root.className = 'v2-root';
  root.style.cssText = 'position: fixed; left: -99999px; top: 0; z-index: -1; pointer-events: none;';
  // Force light-theme tokens: explicitly ensure container does not inherit dark class
  root.classList.remove('dark');

  const styleEl = document.createElement('style');
  styleEl.textContent = PDF_STYLES;
  root.appendChild(styleEl);

  document.body.appendChild(root);

  try {
    await ensureFonts();

    const pages: HTMLElement[] = [];
    pages.push(buildCover(data));

    const promptName = escapeHtml(data.prompt.name || 'Campaign Brief');
    const briefBlocks = buildBriefBlocks(parseBrief(data.prompt.prompt));

    // Measure-and-paginate: build each brief block (paragraph/guideline) off-screen,
    // measure its real height, and partition blocks into pages so cumulative height
    // stays within BRIEF_CONTENT_HEIGHT. A brief used to render on a single page that
    // could grow past A4; html2canvas then captured an over-tall page and jsPDF
    // stamped it at fixed A4 dimensions, squashing it vertically.
    const briefMeasureContainer = document.createElement('div');
    briefMeasureContainer.style.cssText = 'position: absolute; left: -99999px; top: 0; width: 710px; visibility: hidden; pointer-events: none;';
    root.appendChild(briefMeasureContainer);

    let briefPaginated: PaginateResult;
    try {
      briefPaginated = paginateRows(briefBlocks, buildBriefBlockEl, briefMeasureContainer, BRIEF_CONTENT_HEIGHT);
    } finally {
      if (briefMeasureContainer.parentNode) briefMeasureContainer.parentNode.removeChild(briefMeasureContainer);
    }
    const briefPages = briefPaginated.pages.length > 0 ? briefPaginated.pages : [[]];

    let pdfPageNum = 2;
    briefPages.forEach((blockIndices, idx) => {
      const blockEls = blockIndices.map((i) => buildBriefBlockEl(briefBlocks[i]));
      pages.push(buildBriefPage(blockEls, promptName, idx === 0, pdfPageNum));
      pdfPageNum++;
    });

    // Combine: matched first (desc by confidence), then unmatched (desc).
    const matched = [...data.matchedSamples]
      .sort((a, b) => b.confidence - a.confidence)
      .map(s => ({ ...s, isMatch: true }));
    const unmatched = [...data.unmatchedSamples]
      .sort((a, b) => b.confidence - a.confidence)
      .map(s => ({ ...s, isMatch: false }));
    const all = [...matched, ...unmatched];

    // Measure-and-paginate: build each row off-screen, measure its real height,
    // and partition rows into pages so cumulative height stays within
    // PAGE_CONTENT_HEIGHT. Long reasoning text used to be silently clipped by
    // overflow:hidden on a fixed-height page; that's gone now.
    const measureContainer = document.createElement('div');
    measureContainer.style.cssText = 'position: absolute; left: -99999px; top: 0; width: 710px; visibility: hidden; pointer-events: none;';
    root.appendChild(measureContainer);

    let paginated: PaginateResult;
    try {
      paginated = paginateRows(all, buildTableRowEl, measureContainer);
    } finally {
      if (measureContainer.parentNode) measureContainer.parentNode.removeChild(measureContainer);
    }

    paginated.pages.forEach((rowIndices, chunkIdx) => {
      // Re-build elements fresh — the measurement ones were discarded with the
      // measure container, and html2canvas needs them parented to .v2-page.
      const rowEls = rowIndices.map((i) => buildTableRowEl(all[i]));
      pages.push(buildTablePage(rowEls, chunkIdx + 1, all.length, rowIndices.length, pdfPageNum));
      pdfPageNum++;
    });

    pages.forEach(p => root.appendChild(p));

    // Let layout settle.
    await new Promise<void>(resolve => setTimeout(resolve, 50));

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

    for (let i = 0; i < pages.length; i++) {
      const canvas = await html2canvas(pages[i], {
        scale: 2,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: PAGE_W,
        windowHeight: PAGE_H,
      });
      const dataURL = canvas.toDataURL('image/jpeg', 0.92);
      if (i > 0) pdf.addPage();
      pdf.addImage(dataURL, 'JPEG', 0, 0, 210, 297);
    }

    const blob = pdf.output('blob');
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const isPromptExport = data.bulkJob.id.startsWith('prompt-');
    const slug = (data.prompt.name || 'report')
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase() || 'report';
    const date = new Date().toISOString().slice(0, 10);
    link.download = isPromptExport
      ? `prompt-overview-v2-${slug}-${date}.pdf`
      : `analysis-report-v2-${data.bulkJob.id}-${date}.pdf`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } finally {
    if (root.parentNode) root.parentNode.removeChild(root);
  }
}
